import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatCurrency } from '../../utils/formatters';

interface StockMovementDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  movementId: string;
}

interface StockMovementDetail {
  id: string;
  movementType: string;
  quantity: number;
  unitCost?: number;
  totalCost?: number;
  date: string;
  referenceType?: string;
  referenceId?: string;
  referenceNumber?: string;
  fromWarehouseId?: string;
  toWarehouseId?: string;
  notes?: string;
  product: { id: string; name: string; sku: string; };
  warehouse: { id: string; name: string; code: string; };
  fromWarehouse?: { id: string; name: string; code: string; };
  toWarehouse?: { id: string; name: string; code: string; };
  serialNumbers?: Array<{ id: string; serialNumber: string; }>;
}

const panelStyle = (accent: string): React.CSSProperties => ({
  background: '#faf7ee', border: `1px solid ${accent}`,
  borderRadius: 6, padding: '14px 16px',
});

const StockMovementDetailModal: React.FC<StockMovementDetailModalProps> = ({
  isOpen,
  onClose,
  movementId,
}) => {
  const navigate = useNavigate();
  const { data: movement, isLoading, error } = useQuery({
    queryKey: ['stock-movement', movementId],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get(`http://localhost:5002/api/stock-movements/${movementId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data as StockMovementDetail;
    },
    enabled: isOpen && !!movementId,
  });

  const handleViewRelatedDocument = () => {
    if (movement?.referenceType === 'INVOICE' && movement?.referenceId) {
      onClose();
      navigate(`/invoices/${movement.referenceId}`);
    } else if (movement?.referenceType === 'BILL' && movement?.referenceId) {
      onClose();
      navigate(`/bills/${movement.referenceId}`);
    }
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const refTypeLabel: Record<string, string> = {
    MANUAL: 'Manual Entry',
    TRANSFER: 'Warehouse Transfer',
    INVOICE: 'Invoice Sale',
    BILL: 'Bill Purchase',
    ADJUSTMENT: 'Stock Adjustment',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)',
        overflowY: 'auto', zIndex: 50, display: 'flex',
        alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: 80, paddingBottom: 40,
      }}
      onClick={handleBackdropClick}
    >
      <div style={{
        background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)',
        borderRadius: 8, width: '100%', maxWidth: 720, padding: '28px 32px',
        position: 'relative',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid rgba(92,78,51,0.15)',
        }}>
          <div>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300, color: '#1c2033', margin: '0 0 4px' }}>
              Stock Movement Details
            </h3>
            {movement && (
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                Transaction: {movement.referenceNumber || movement.id}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
              color: '#6b6a5c', cursor: 'pointer', padding: '4px 8px', fontSize: 16, lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c' }}>
              Loading transaction details...
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={{
            background: 'rgba(201,90,90,0.08)', border: '1px solid rgba(201,90,90,0.2)',
            borderRadius: 6, padding: '12px 16px', marginBottom: 16,
          }}>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a', margin: 0 }}>
              Failed to load transaction details. Please try again.
            </p>
          </div>
        )}

        {/* Content */}
        {movement && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Movement Type Badges */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                padding: '5px 14px',
                background: movement.movementType === 'IN' ? 'rgba(107,191,138,0.12)' : 'rgba(201,90,90,0.12)',
                border: `1px solid ${movement.movementType === 'IN' ? 'rgba(107,191,138,0.3)' : 'rgba(201,90,90,0.3)'}`,
                borderRadius: 20,
                fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase',
                color: movement.movementType === 'IN' ? '#6bbf8a' : '#c95a5a',
              }}>
                {movement.movementType === 'IN' ? 'Stock Receipt (IN)' : 'Stock Issue (OUT)'}
              </span>
              {movement.referenceType && (
                <span style={{
                  padding: '4px 10px',
                  background: 'rgba(239,232,213,0.6)', border: '1px solid rgba(92,78,51,0.25)',
                  borderRadius: 20,
                  fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5b5a52',
                }}>
                  {refTypeLabel[movement.referenceType] || movement.referenceType}
                </span>
              )}
            </div>

            {/* Transaction Information */}
            <div style={panelStyle('rgba(92,78,51,0.15)')}>
              <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706', marginBottom: 12, marginTop: 0 }}>
                Transaction Information
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 4px' }}>Date &amp; Time</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>
                    {new Date(movement.date).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 4px' }}>Reference Number</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>
                    {movement.referenceNumber || '—'}
                  </p>
                </div>
                <div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 4px' }}>Quantity</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 600, color: '#d97706', margin: 0 }}>
                    {movement.quantity}
                  </p>
                </div>
                {movement.unitCost !== undefined && movement.unitCost > 0 && (
                  <div>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 4px' }}>Unit Cost</p>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>
                      {formatCurrency(movement.unitCost)}
                    </p>
                  </div>
                )}
                {movement.totalCost !== undefined && movement.totalCost > 0 && (
                  <div>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 4px' }}>Total Cost</p>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>
                      {formatCurrency(movement.totalCost)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Product Information */}
            <div style={panelStyle('rgba(90,140,201,0.15)')}>
              <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#5a8cc9', marginBottom: 12, marginTop: 0 }}>
                Product Information
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 4px' }}>Product Name</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>{movement.product.name}</p>
                </div>
                <div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 4px' }}>SKU</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>{movement.product.sku}</p>
                </div>
              </div>
            </div>

            {/* Warehouse Information */}
            <div style={panelStyle('rgba(176,122,220,0.15)')}>
              <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#b07adc', marginBottom: 12, marginTop: 0 }}>
                Warehouse Information
              </h4>
              {movement.referenceType === 'TRANSFER' && movement.fromWarehouse && movement.toWarehouse ? (
                <div>
                  <div style={{ marginBottom: 8 }}>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 4px' }}>From Warehouse</p>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>
                      {movement.fromWarehouse.name} <span style={{ color: '#6b6a5c' }}>({movement.fromWarehouse.code})</span>
                    </p>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
                    <svg width="16" height="16" fill="none" stroke="#8a8778" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                  <div>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 4px' }}>To Warehouse</p>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>
                      {movement.toWarehouse.name} <span style={{ color: '#6b6a5c' }}>({movement.toWarehouse.code})</span>
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', margin: '0 0 4px' }}>Warehouse</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>
                    {movement.warehouse.name} <span style={{ color: '#6b6a5c' }}>({movement.warehouse.code})</span>
                  </p>
                </div>
              )}
            </div>

            {/* Serial Numbers */}
            {movement.serialNumbers && movement.serialNumbers.length > 0 && (
              <div style={panelStyle('rgba(92,78,51,0.25)')}>
                <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#d97706', marginBottom: 12, marginTop: 0 }}>
                  Serial Numbers
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {movement.serialNumbers.map((sn, index) => (
                    <span
                      key={sn.id || index}
                      style={{
                        padding: '4px 10px',
                        background: '#f4efe2', border: '1px solid rgba(92,78,51,0.3)',
                        borderRadius: 4,
                        fontFamily: 'monospace', fontSize: 12, color: '#5b5a52',
                      }}
                    >
                      {sn.serialNumber}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {movement.notes && (
              <div style={panelStyle('rgba(92,78,51,0.15)')}>
                <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', marginBottom: 8, marginTop: 0 }}>
                  Notes
                </h4>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#5b5a52', margin: 0 }}>
                  {movement.notes}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              paddingTop: 16, borderTop: '1px solid rgba(92,78,51,0.15)',
            }}>
              {movement.referenceType && ['BILL', 'INVOICE'].includes(movement.referenceType) && movement.referenceId ? (
                <button
                  onClick={handleViewRelatedDocument}
                  style={{
                    padding: '9px 18px', background: 'rgba(90,140,201,0.08)',
                    border: '1px solid rgba(90,140,201,0.25)', borderRadius: 4,
                    fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500,
                    letterSpacing: '0.15em', textTransform: 'uppercase', color: '#5a8cc9', cursor: 'pointer',
                  }}
                >
                  View Related {movement.referenceType === 'BILL' ? 'Bill' : 'Invoice'}
                </button>
              ) : <div />}
              <button
                onClick={onClose}
                style={{
                  padding: '9px 24px', background: 'none',
                  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
                  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 400,
                  letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockMovementDetailModal;
